import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";

/**
 * Subscribe to Supabase realtime updates on a list of tables.
 * When any of those tables changes (INSERT/UPDATE/DELETE), invalidate the
 * React Query cache for the given queryKey prefixes so they refetch.
 *
 * Result: the UI updates within ~1 second of new data landing in Supabase,
 * instead of waiting for React Query's 60-second background refetch.
 *
 * Prerequisite: the tables must be added to the supabase_realtime publication.
 * See the SQL block in the README / chat. Run once per table:
 *   ALTER PUBLICATION supabase_realtime ADD TABLE mirror_disptech;
 *
 * Usage:
 *   useRealtimeRefresh(
 *     ["mirror_disptech", "mirror_dispatch"],
 *     [["board_dispatches"], ["dispatches", "today"]],
 *   );
 */
export function useRealtimeRefresh(
  tables: string[],
  queryKeyPrefixes: ReadonlyArray<ReadonlyArray<string>>,
) {
  const queryClient = useQueryClient();
  // INVOICE-LIVE-01: supabase-js hands back the EXISTING channel when two
  // hooks ask for the same topic — the second .on() then lands on an already
  // subscribed channel and the first unmount tears down both. One channel per
  // hook instance.
  const instance = useRef(Math.random().toString(36).slice(2, 10));

  useEffect(() => {
    const channelName = `realtime-${tables.join("-")}-${instance.current}`;
    const channel = supabase.channel(channelName);

    // DEBOUNCED invalidation (AUD-07 root cause): the 5-minute ESC sync
    // upserts ~12k mirror rows, which arrives here as THOUSANDS of
    // postgres_changes events in a burst. Invalidating per-event re-fetched
    // and re-rendered the board hundreds of times back-to-back and pegged the
    // main thread (the "frozen page" episodes). A trailing window turns any
    // burst into ONE refetch ~1.5s after it ends — still feels instant for
    // single-row writes (texts, calls), and bulk syncs cost one render.
    let timer: number | undefined;
    const invalidate = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        for (const prefix of queryKeyPrefixes) {
          queryClient.invalidateQueries({ queryKey: [...prefix] });
        }
      }, 1500);
    };

    for (const table of tables) {
      channel.on(
        "postgres_changes" as never,
        { event: "*", schema: "public", table },
        invalidate,
      );
    }

    channel.subscribe();
    return () => {
      if (timer) window.clearTimeout(timer);
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables.join(","), JSON.stringify(queryKeyPrefixes)]);
}
